import { call, put, select, race, take, fork } from 'redux-saga/effects';
import { restoreLayout, ActivateCellAction, seek } from 'actions/monitoringActions';
import { CellState, durableCellStateProperties, DurableCellState, CellIndex, DeviceIdMap } from 'types';
import { getCells } from 'selectors/monitoringSelectors';
import { isChangeImage } from 'selectors/shellSelectors';
import { emptyCell } from 'reducers/monitoringReducer';
import { makeStreamId } from 'util/streamId';
import { getDeviceIdByDeviceIndex } from 'selectors/deviceSelectors';
import { getRequestedCameraAndTime, getRequestedView } from 'selectors/appSelectors';
import { times } from 'util/times';

const localStorageKeys = {
    cells: 'layout-cells',
    activeCell: 'layout-activeCell',
};

export function* layoutChangeWatcher() {
    while (true) {
        const { activateCell, ...winner } = yield race({
            setLayout: take('SET_LAYOUT'),
            activateCell: take('ACTIVATE_CELL'),
            assignStream: take('ASSIGN_STREAM'),
            setZoom: take('SET_ZOOM'),
            setScroll: take('SET_SCROLL'),
            setSync: take('SET_SYNC'),
            setStreamPosition: take('SET_STREAM_POSITION'), // TODO: throttle this if this becomes too frequent... 
        });

        if (activateCell) {
            yield fork(saveActiveCellSaga, (activateCell as ActivateCellAction).payload);
        }
        else {
            const action = winner[Object.keys(winner)[0]]; // Tricky way to get the winning action
            const cellIndex: CellIndex = action.meta && action.meta.cellIndex || action.payload.cellIndex;
            if (cellIndex === 0) {
                // We don't persist cell 0, which is for the Change Image dialog
                continue;
            }

            yield fork(saveCellsSaga);
        }
    }
}

export function* saveActiveCellSaga(cellIndex: CellIndex) {
    if (cellIndex === 0) {
        // We don't persist cell 0, which is for the Change Image dialog
        return;
    }
    yield call([ window.localStorage, 'setItem' ], localStorageKeys.activeCell, cellIndex.toString());
}

export function* saveCellsSaga() {
    const cells: ReadonlyArray<CellState> = yield select(getCells);

    // Convert cells to durable cells. Ignore the first cell (it's for the Change Image dialog -- we don't persist it)
    const durableCells = cells.slice(1).map(c => {
        // Clone the current cell and remove properties that are not durable
        const durableCell = { ...c };
        Object.keys(durableCell).forEach(propName => {
            if (durableCellStateProperties.indexOf(propName as any) < 0) { // tslint:disable-line:no-any
                delete durableCell[propName];
            }
        });
        return durableCell as DurableCellState;
    });

    yield call([ window.localStorage, 'setItem' ], localStorageKeys.cells, JSON.stringify(durableCells));
}

export function* initLayoutSaga() {
    // Skip if we're using the Change Image screen (e.g. from Server Config)
    if (yield select(isChangeImage)) {
        return;
    }

    // Jump to the specified camera and time if we're handling an Alarm URL
    let { deviceIndex, deviceId, utcTime } = yield select(getRequestedCameraAndTime);
    if (deviceIndex) {
        // If caller specified DeviceIndex, translate to DeviceId
        const deviceIdByDeviceIndex: DeviceIdMap = yield select(getDeviceIdByDeviceIndex);
        deviceId = deviceIdByDeviceIndex[`${deviceIndex}`];
    }
    if (deviceId && utcTime) {
        const streamId =
            deviceId
                ? makeStreamId(deviceId, 0)
                : null;
        yield put(restoreLayout({
            durableCells: [
                {
                    ...emptyCell,
                    streamId,
                    streamPosition: utcTime,
                    live: false,
                    playing: false,
                } as DurableCellState,
            ],
            activeCellIndex: 1,
        }));
        yield put(seek({ cellIndex: 1, utcTime }));
        return;
    }

    // AIR-36320
    // Jump to the specified view if we're handling a preconfigured view URL from ServerConfig
    const viewDeviceIndexes = yield select(getRequestedView);
    if (viewDeviceIndexes) {
        const deviceIdByDeviceIndex: DeviceIdMap = yield select(getDeviceIdByDeviceIndex);
        let durableCells = times(16, i => {
            if (i >= viewDeviceIndexes.length) {
                return emptyCell;
            }

            const deviceId = deviceIdByDeviceIndex[`${viewDeviceIndexes[i]}`];
            const streamId = makeStreamId(deviceId, 0);
            return {
                ...emptyCell,
                streamId,
                live: true,
                playing: true,
            };
        });

        // Trim down the durable cells to the minimum layout size that will show all cells
        if (viewDeviceIndexes.length >= 10) {
            // Do nothing
        }
        else if (viewDeviceIndexes.length >= 5 && viewDeviceIndexes.length <= 9) {
            durableCells = durableCells.slice(0, 9);
        }
        else if (viewDeviceIndexes.length >= 2) {
            durableCells = durableCells.slice(0, 4);
        }
        else {
            durableCells = durableCells.slice(0, 1);
        }

        yield put(restoreLayout({
            durableCells,
            activeCellIndex: 1,
        }));
        return;
    }


    //
    // Otherwise, perform the standard layout restore from local storage
    //

    const activeCellIndexString = yield call([ window.localStorage, 'getItem' ], localStorageKeys.activeCell);
    const activeCellIndex = parseInt(activeCellIndexString || '1', 10) as CellIndex;

    const durableCellsString = yield call([ window.localStorage, 'getItem' ], localStorageKeys.cells);
    let durableCells: DurableCellState[] = [];
    try {
        durableCells = JSON.parse(durableCellsString);
        if (!durableCells) {
            return;
        }
    }
    catch (err) {
        yield call([ console, 'error' ], 'Failed to restore layout');
        return;
    }
    yield put(restoreLayout({ durableCells, activeCellIndex }));
}



// WEBPACK FOOTER //
// ./src/sagas/monitoring/layoutSagas.ts